import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import {
  Button,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Portal,
  Card,
  classNames,
  noop,
} from '@panwds/react-ui'
import { PushpinEmptyIcon, PushpinIcon } from '@panwds/icons'
import { FocusOn } from 'react-focus-on'
import { CloseIcon } from '@panwds/icons'
import { actions, getRootElement } from '@sparky/framework'
import TSGTree from './TSGTree'
import type { CSSProperties, FC, VFC } from 'react'
import type { Tsg, ReadonlyTsgTree } from '@sparky/framework'
import type { ModalProps } from '@panwds/react-ui'

const TENANT_SWITCHER_TITLE = 'Tenants'
const LOGOUT_TITLE = 'Sign Out'

export const useShouldRender = (isOpen: boolean): boolean => {
  const [shouldRenderChildren, setShouldRenderChildren] =
    useState<boolean>(isOpen)

  useEffect(() => {
    let timeout: number

    if (isOpen) {
      setShouldRenderChildren(true)
    }
    else {
      timeout = window.setTimeout(() => {
        setShouldRenderChildren(false)
      }, 200)
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [isOpen])

  return isOpen || shouldRenderChildren
}

const TenantSwitcherModal: FC<ModalProps & { autoClose: boolean, addClassName?: string }> = ({
  children,
  addClassName,
  onClose,
  isOpen,
  autoClose,
  style,
  shards,
}) => {
  const shouldRender = useShouldRender(isOpen)

  if (!shouldRender && !isOpen) {
    return null
  }

  const opacity0 = !shouldRender || !isOpen

  const dialogClassName = classNames(
    'cm-fixed cm-bg-white cm-transform cm-translate-y-0 cm-z-10 cm-max-w-full cm-transition-all cm-duration-200',
    opacity0 ? 'cm-opacity-0' : 'cm-opacity-100',
    addClassName,
  )

  const card = <Card
    addClassName={dialogClassName}
    aria-modal={true}
    role='dialog'
    style={{
      zIndex: 15,
      top: 50,
      width: 330,
      ...style,
      minHeight: opacity0 ? 0 : 100,
      maxHeight: opacity0 ? 0 : style?.maxHeight,
      outline: 'none',
    } as CSSProperties}
  >
    {children}
  </Card>

  const portalInner = autoClose ? (
    <FocusOn
      noIsolation
      onClickOutside={onClose}
      onEscapeKey={onClose}
      shards={shards}
    >{card}</FocusOn>
  ) : card

  return (
    <Portal dataMetrics='sparky-hub/tenant-switcher' dataTestId='sparky-hub-tenant-switcher'>
      {portalInner}
    </Portal>
  )
}

const TenantSwitcher: VFC<{ tsg: Tsg | undefined, tsgs: ReadonlyTsgTree, isOpen: boolean, setOpen: (isOpen: boolean) => void, close: () => void }> = ({ tsg, tsgs, isOpen, setOpen, close }) => {
  const tsgId = tsg?.id
  const tsgIdRef = useRef<string | undefined>(tsgId)
  tsgIdRef.current = tsgId
  const [loadingTsgId, setLoadingTsgId] = useState<string | undefined>()
  const [pinned, setPinned] = useState<boolean>(false)

  const selectedTsgId = useMemo<string | undefined>(() => (
    loadingTsgId ? loadingTsgId : tsgId ? tsgId : undefined
  ), [loadingTsgId, tsgId])

  const autoClose = pinned ? noop : close // for pinned case
  const selectTsg = useCallback((nextTsgId: string | undefined) => {
    if (nextTsgId && tsgIdRef.current !== nextTsgId) {
      setLoadingTsgId(nextTsgId)
      actions.switchTsg(nextTsgId).then(() => setLoadingTsgId(undefined))
    }
  }, [])

  const onSelect = useCallback((tsgId: string | undefined) => {
    selectTsg(tsgId)
    autoClose()
  }, [selectTsg, autoClose])

  useEffect(() => {
    const pinnedPref = window.sessionStorage.pinned_tenant_switcher
    if (tsgs.count > 1 ? pinnedPref !== '0' : pinnedPref === '1') {
      setPinned(true)
      setOpen(true)
    }
  }, [tsgs, setPinned, setOpen])

  const clearPinClose = useCallback(() => {
    setPinned(false)
    close()
  }, [close])

  const noTsg = !tsgs.children?.length

  const footer = useMemo(() => (
    <ModalFooter secondaryContent={
      <Button
        appearance='secondary'
        onClick={actions.logout}>
        {LOGOUT_TITLE}
      </Button>
    }>{null}</ModalFooter>
  ), [])

  useEffect(() => {
    const parent = getRootElement()?.querySelector('.sparky-hub-content')
    parent?.classList?.[pinned ? 'add' : 'remove']('sparky-hub-pinned')
    window.sessionStorage.pinned_tenant_switcher = pinned ? '1' : '0'
  }, [pinned])

  return <TenantSwitcherModal
    size='sm'
    autoClose={!pinned}
    onClose={autoClose}
    isOpen={isOpen}
    addClassName={pinned ? '' : 'cm-drop-shadow-lg'}
    style={{
      '--tw-translate-x': pinned ? 'calc(100vw - 100%)' : 'calc(100vw - 100% - 1em)',
      maxHeight: pinned ? 'calc(100vh - 50px)' : 'calc(100vh - 50px - 1em)',
    } as CSSProperties}
  >
    <ModalHeader
      title={TENANT_SWITCHER_TITLE}
      enableClose={false}
      actions={<>
        <Button
          icon={pinned ? <PushpinIcon size='sm' /> : <PushpinEmptyIcon size='sm' />}
          size='sm'
          appearance='tertiary-clear'
          aria-label='Pin'
          onClick={useCallback(() => {
            setPinned(!pinned)
          }, [pinned])}
        />
        <Button
          appearance='tertiary-clear'
          aria-label='Close'
          icon={<CloseIcon size='sm' className='tw-absolute' />}
          size='sm'
          onClick={pinned ? clearPinClose : close}
        />
      </>}
    />
    {noTsg ? (
      <ModalBody addClassName='cm-flex cm-flex-col cm-w-full cm-p-0 cm-overflow-hidden'>
        <div className='cm-items-center cm-text-center cm-p-3'>
          No tenant is available.
        </div>
      </ModalBody>
    ) : (
      <ModalBody addClassName='cm-flex cm-flex-col cm-w-full cm-p-0 cm-overflow-hidden'>
        <TSGTree {...{ tsg, tsgs, onSelect, selectedTsgId, isVisible: isOpen, hasAddBtn: false }} />
      </ModalBody>
    )}
    {footer}
  </TenantSwitcherModal>
}

export default TenantSwitcher
